import { ChangeDetectionStrategy, Component, input, signal } from '@angular/core';
import { AbstractControl, FormArray, ReactiveFormsModule } from '@angular/forms';
import { LookupResponse } from '@core/models/common.model';
import { ScaleSliderComponent } from '@shared/components/scale-slider/scale-slider.component';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';
import { ClinicalBackgroundGroup } from '../../cia-form-groups';
import { FOUR_P_HINTS, ordered } from '../../cia-lookups';
import { CiaStepBase } from '../../cia-step-base';

// Step 2, Clinical assessment. The 4P grid is a table with its column headings written once, the life stages fold away until one is
// opened, and the patterns are rated side by side with their notes a click away.
@Component({
  selector: 'cia-step-background',
  imports: [ReactiveFormsModule, ScaleSliderComponent, ...SharedMaterialUIModule],
  templateUrl: './cia-step-background.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class CiaStepBackgroundComponent extends CiaStepBase {
  // The step's form group, held by the form so its validity counts there.
  readonly group = input.required<ClinicalBackgroundGroup>();

  // The patterns whose note box has been opened (one that already has text is always open).
  private readonly openedNotes = signal<ReadonlySet<number>>(new Set());

  // The three row sets of this step, as untyped arrays for the template.
  protected factorRows(): FormArray {
    return this.group().controls.fourPFactors as FormArray;
  }

  protected stageRows(): FormArray {
    return this.group().controls.developmentStages as FormArray;
  }

  protected patternRows(): FormArray {
    return this.group().controls.patternRatings as FormArray;
  }

  // The four 4P factors (the grid's rows) and the three sides (its columns), in the order of the review form.
  protected factorTypes(): LookupResponse[] {
    return ordered(this.lookups(), 'fourPFactorTypes');
  }

  protected dimensionTypes(): LookupResponse[] {
    return ordered(this.lookups(), 'fourPDimensionTypes');
  }

  // The tooltip that explains a 4P factor.
  protected factorHint(code: string | null): string {
    return (code && FOUR_P_HINTS[code]) || '';
  }

  // A life stage has something written in it (it then shows a tick).
  protected stageFilled(row: AbstractControl): boolean {
    return !!String(row.value.notes ?? '').trim();
  }

  // A pattern's note box is shown when it was opened or already has text.
  protected noteOpen(index: number, row: AbstractControl): boolean {
    return this.openedNotes().has(index) || !!String(row.value.notes ?? '').trim();
  }

  // Opens the note box of one pattern.
  protected openNote(index: number): void {
    this.openedNotes.update((current) => new Set(current).add(index));
  }
}
